import React, { useEffect, useState, useCallback } from "react";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { photoUrl } from "@/lib/api";

/**
 * Fullscreen lightbox with keyboard nav, swipe, and preloading.
 * Usage: <PhotoLightbox photos={[{path,caption}]} startIndex={0} onClose={()=>{}} />
 * `photos` can be array of strings (legacy) or array of {path, caption}.
 */
export default function PhotoLightbox({ photos, startIndex = 0, onClose, captions = {} }) {
  const [idx, setIdx] = useState(startIndex);
  const total = photos?.length || 0;

  const normalize = (p) => (typeof p === "string" ? { path: p, caption: "" } : p);
  const current = total > 0 ? normalize(photos[idx]) : null;
  const currentCaption = current?.caption || captions[String(idx)] || "";

  const prev = useCallback(() => setIdx((i) => (i > 0 ? i - 1 : total - 1)), [total]);
  const next = useCallback(() => setIdx((i) => (i < total - 1 ? i + 1 : 0)), [total]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose?.();
      if (e.key === "ArrowLeft") prev();
      if (e.key === "ArrowRight") next();
    };
    window.addEventListener("keydown", onKey);
    document.body.style.overflow = "hidden";
    return () => { window.removeEventListener("keydown", onKey); document.body.style.overflow = ""; };
  }, [prev, next, onClose]);

  // Preload neighbors
  useEffect(() => {
    if (!photos) return;
    [idx - 1, idx + 1].forEach((i) => {
      if (i < 0 || i >= total) return;
      const img = new Image();
      img.src = photoUrl(normalize(photos[i]).path);
    });
  }, [idx, photos, total]);

  // Swipe support (touch)
  const [touchStart, setTouchStart] = useState(null);
  const onTouchStart = (e) => setTouchStart(e.changedTouches[0].screenX);
  const onTouchEnd = (e) => {
    if (touchStart == null) return;
    const delta = e.changedTouches[0].screenX - touchStart;
    if (Math.abs(delta) > 50) (delta < 0 ? next() : prev());
    setTouchStart(null);
  };

  if (!current) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Fotogalleri"
      className="fixed inset-0 z-[100] bg-black/95 flex flex-col animate-fade-in"
      data-testid="photo-lightbox"
      onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}
      onTouchStart={onTouchStart}
      onTouchEnd={onTouchEnd}
    >
      <div className="absolute top-4 right-4 z-10 flex items-center gap-2">
        <span className="text-white/70 text-xs" data-testid="lightbox-counter">{idx + 1} / {total}</span>
        <button onClick={onClose} className="text-white/90 hover:text-white bg-white/10 hover:bg-white/20 rounded-full p-2" aria-label="Luk" data-testid="lightbox-close">
          <X className="h-5 w-5" />
        </button>
      </div>

      {total > 1 && (
        <>
          <button onClick={prev} className="hidden sm:flex absolute left-4 top-1/2 -translate-y-1/2 z-10 bg-white/10 hover:bg-white/20 text-white rounded-full p-3 focus:outline-none focus:ring-2 focus:ring-white/60" aria-label="Forrige foto" data-testid="lightbox-prev">
            <ChevronLeft className="h-6 w-6" />
          </button>
          <button onClick={next} className="hidden sm:flex absolute right-4 top-1/2 -translate-y-1/2 z-10 bg-white/10 hover:bg-white/20 text-white rounded-full p-3 focus:outline-none focus:ring-2 focus:ring-white/60" aria-label="Næste foto" data-testid="lightbox-next">
            <ChevronRight className="h-6 w-6" />
          </button>
        </>
      )}

      <div className="flex-1 flex items-center justify-center px-4 pb-16 pt-14">
        <img
          key={current.path}
          src={photoUrl(current.path)}
          alt=""
          loading="eager"
          className="max-w-full max-h-full object-contain animate-fade-in"
        />
      </div>

      {currentCaption && (
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 max-w-[85vw] text-center bg-black/60 backdrop-blur text-white text-sm px-4 py-2 rounded-full" data-testid="lightbox-caption">
          {currentCaption}
        </div>
      )}

      {total > 1 && (
        <div className="absolute bottom-2 left-1/2 -translate-x-1/2 flex gap-1.5">
          {photos.map((_, i) => (
            <button
              key={i}
              onClick={() => setIdx(i)}
              className={`h-1.5 rounded-full transition-all ${i === idx ? "bg-white w-6" : "bg-white/40 w-1.5 hover:bg-white/70"}`}
              aria-label={`Foto ${i + 1}`}
            />
          ))}
        </div>
      )}
    </div>
  );
}
